import { useCallback, useEffect } from 'react';
import { Y200 } from '@atlaskit/theme/colors';
import { fg } from '@atlassian/jira-feature-gating';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import type { ProjectStyle } from '../../../../common/constants/project';
import type { RequestAccessTokenMethod, ThirdPartyEntity } from '../../../../common/types';
import { useContentForThirdParty } from '../../../../common/utils';
import { useProjectData, useThirdPartyState } from '../../../../controllers/import-state';
import { useThirdPartySubfolders } from '../../../../controllers/import-state/selectors';
import { useSearchThirdPartyEntities } from '../../../../services/search-third-party-entities';
import { useThirdPartyEntities } from '../../../../services/third-party-entities';
import { useThirdPartyEntitiesPage } from '../../../../services/third-party-entities-page';

type Props = {
	thirdParty: SupportedThirdParty;
	shouldShowPlaceholderIcons: boolean;
	hasSearchableEntities: boolean;
	requestAuthenticationToken: RequestAccessTokenMethod;
	hideWorkspaceSelector: boolean;
	parentEntityId: string | null;
};

export const useEntities = ({
	thirdParty,
	shouldShowPlaceholderIcons,
	hasSearchableEntities,
	requestAuthenticationToken,
	hideWorkspaceSelector,
	parentEntityId,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		entitySelectorLabel: label,
		entitySelectorPlaceholder: placeholder,
		starredEntitiesLabel: starredOptionsLabel,
		otherEntitiesLabel: otherOptionsLabel,
		noEntitiesFoundHeader,
		noEntitiesFoundDescription,
		noEntitiesFoundWithFolderDescription,
		noEntitiesFoundWithSubfolderDescription,
		errorOccurredFetchingEntitiesHeader: errorOccurredHeader,
		searchEntitiesErrorOccurredDescription: searchErrorOccurredDescription,
		loadingMoreEntitiesErrorOccurredDescription: loadingMoreErrorOccurredDescription,
	} = useContentForThirdParty(thirdParty);

	const { fetchThirdPartyEntities, hasErrorOccurred, isLoading } = useThirdPartyEntities({
		thirdParty,
		requestAuthenticationToken,
	});

	const [
		{
			entities,
			selectedEntities,
			selectedEntityId,
			selectedEntityName,
			selectedWorkspaceId,
			selectedSpaceId,
			workspaces,
			selectedFolderId,
			selectedSubfolderId,
		},
		{ setThirdPartyState, setThirdPartyStateWithPreselectedEntityId },
	] = useThirdPartyState();
	const [{ selectedFolderIds }] = useThirdPartySubfolders();

	const [, { setProjectData }] = useProjectData();

	const {
		searchThirdPartyEntities,
		hasErrorOccurred: hasSearchEntitiesErrorOccurred,
		resetSearchError,
	} = useSearchThirdPartyEntities({
		thirdParty,
		requestAuthenticationToken,
	});

	const {
		fetchThirdPartyEntitiesPage,
		hasErrorOccurred: hasEntityPageErrorOccurred,
		isLoading: isEntitiesPageLoading,
		fetchNextPage,
	} = useThirdPartyEntitiesPage({ thirdParty, requestAuthenticationToken });

	const canFetchEntities =
		hideWorkspaceSelector ||
		(workspaces !== null &&
			(fg('jwm_clickup_importer') ? parentEntityId !== null : selectedWorkspaceId !== null));

	const onSearchEntities = useCallback(
		(query: string) => {
			if (!canFetchEntities) {
				return Promise.resolve([]);
			}
			return searchThirdPartyEntities({
				workspaceId: selectedWorkspaceId || undefined,
				query,
			});
		},
		[canFetchEntities, searchThirdPartyEntities, selectedWorkspaceId],
	);

	const fetchEntities = useCallback(() => {
		if (!canFetchEntities || entities !== null) {
			return;
		}
		if (hasSearchableEntities) {
			fetchThirdPartyEntitiesPage({ workspaceId: selectedWorkspaceId || undefined }).then(
				(thirdPartyEntities) => {
					setThirdPartyStateWithPreselectedEntityId({
						entities: thirdPartyEntities,
						preSelectedEntityId: selectedEntityId,
					});
					fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyEntitiesPage retrieved', {
						thirdParty,
						numEntities: thirdPartyEntities?.length || 0,
					});
				},
			);
		} else {
			fetchThirdPartyEntities({
				workspaceId: selectedWorkspaceId || undefined,
				folderIds: selectedFolderIds || undefined,
				...(fg('jwm_clickup_importer') ? { spaceId: selectedSpaceId || undefined } : {}),
			}).then((thirdPartyEntities) => {
				if (thirdPartyEntities !== null) {
					setThirdPartyStateWithPreselectedEntityId({
						entities: thirdPartyEntities,
						preSelectedEntityId: selectedEntityId,
					});
					fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyEntities retrieved', {
						thirdParty,
						numEntities: thirdPartyEntities?.length || 0,
					});
				}
			});
		}
	}, [
		canFetchEntities,
		entities,
		hasSearchableEntities,
		fetchThirdPartyEntitiesPage,
		selectedWorkspaceId,
		setThirdPartyStateWithPreselectedEntityId,
		selectedEntityId,
		createAnalyticsEvent,
		thirdParty,
		selectedFolderIds,
		selectedSpaceId,
		fetchThirdPartyEntities,
	]);

	useEffect(() => {
		fetchEntities();
	}, [fetchEntities]);

	const onChange = useCallback(
		(entityId: string, entityName: string, style?: ProjectStyle) => {
			setThirdPartyState({
				selectedEntityId: entityId,
				entityStats: null,
				selectedEntityName: entityName,
				selectedEntityStyle: style,
				members: null,
				thirdPartyFieldConfigState: null,
				thirdPartyStatusMapping: null,
			});
			setProjectData({ name: undefined, accessLevel: undefined });
		},
		[setThirdPartyState, setProjectData],
	);

	const onChangeMultipleEntities = useCallback(
		(updateEntities: ThirdPartyEntity[]) => {
			setThirdPartyState({
				selectedEntities: updateEntities,
				thirdPartyFieldConfigState: null,
				thirdPartyStatusMapping: null,
				entityStats: null,
			});
			setProjectData({ name: undefined, accessLevel: undefined });
		},
		[setThirdPartyState, setProjectData],
	);

	const onMenuScrollToBottom = useCallback(() => {
		if (!canFetchEntities || (hasSearchableEntities ? isEntitiesPageLoading : isLoading)) {
			return;
		}
		fetchNextPage(selectedWorkspaceId || undefined).then((thirdPartyEntities) => {
			thirdPartyEntities && setThirdPartyState({ entities: entities?.concat(thirdPartyEntities) });
			fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyEntitiesPage retrieved', {
				thirdParty,
				numEntities: thirdPartyEntities?.length || 0,
				loadNextPage: true,
			});
		});
	}, [
		canFetchEntities,
		createAnalyticsEvent,
		entities,
		fetchNextPage,
		hasSearchableEntities,
		isEntitiesPageLoading,
		isLoading,
		selectedWorkspaceId,
		setThirdPartyState,
		thirdParty,
	]);

	const isLoadingEntities = hasSearchableEntities ? isEntitiesPageLoading : isLoading;
	let noEntitiesFound = noEntitiesFoundDescription;
	if (selectedSubfolderId !== null && noEntitiesFoundWithSubfolderDescription) {
		noEntitiesFound = noEntitiesFoundWithSubfolderDescription;
	} else if (selectedFolderId !== null && noEntitiesFoundWithFolderDescription) {
		noEntitiesFound = noEntitiesFoundWithFolderDescription;
	}

	return {
		label,
		placeholder,
		starredOptionsLabel,
		otherOptionsLabel,
		noEntitiesFoundHeader,
		noEntitiesFoundDescription: noEntitiesFound,
		errorOccurredHeader,
		entities,
		selectedEntities,
		selectedEntityId,
		selectedEntityName,
		isLoading: isLoadingEntities,
		hasErrorOccurred: hasSearchableEntities ? hasEntityPageErrorOccurred : hasErrorOccurred,
		onChange,
		onRetry: fetchEntities,
		isDisabled: !canFetchEntities,
		placeholderColor: shouldShowPlaceholderIcons ? Y200 : undefined,
		onSearchEntities,
		hasSearchErrorOccurred: hasSearchEntitiesErrorOccurred,
		searchErrorOccurredDescription,
		loadingMoreErrorOccurredDescription,
		onMenuScrollToBottom,
		onResetSearchError: resetSearchError,
		isEmptyEntities: !isLoadingEntities && entities?.length === 0,
		onChangeMultipleEntities,
	};
};
